/**
 * Thin REST wrapper around the backend team endpoints. Used by the
 * TanStack Query hooks in ./queries.ts. We hit the same Axios instance
 * the rest of the app uses, so 401 refresh and credentials work the
 * same way.
 */

import type {
  Collaborator,
  CollaboratorAddRequest,
  CollaboratorList,
  CollaboratorUpdateRequest,
  Invitation,
  InvitationList,
  InvitationRequest,
  JoinRequest,
  JoinRequestCreate,
  JoinRequestDecision,
  JoinTargets,
  OrgMemberList,
  OrgRole,
  Organization,
  OrganizationUpdateRequest,
  ReviewGrant,
  ReviewGrantList,
  ReviewGrantRequest,
} from './types';
import apiClient from '@/api';

// ---------- Organization ----------

export async function fetchOrganization(): Promise<Organization> {
  const { data } = await apiClient.get<Organization>('/team/organization');
  return data;
}

export async function updateOrganization(
  body: OrganizationUpdateRequest,
): Promise<Organization> {
  const { data } = await apiClient.patch<Organization>(
    '/team/organization',
    body,
  );
  return data;
}

export async function deleteOrganization(): Promise<void> {
  await apiClient.delete('/team/organization');
}

// ---------- Member management ----------

export async function changeMemberRole(
  userId: string,
  role: OrgRole,
): Promise<void> {
  await apiClient.patch(`/team/members/${userId}`, { role });
}

export async function removeMember(userId: string): Promise<void> {
  await apiClient.delete(`/team/members/${userId}`);
}

export async function transferAdmin(targetUserId: string): Promise<void> {
  await apiClient.post('/team/transfer-admin', {
    target_user_id: targetUserId,
  });
}

export async function leaveOrganization(): Promise<void> {
  await apiClient.post('/team/leave');
}

export async function fetchMembers(
  includeInactive = false,
): Promise<OrgMemberList> {
  const { data } = await apiClient.get<OrgMemberList>('/team/members', {
    params: includeInactive ? { include_inactive: true } : undefined,
  });
  return data;
}

export async function fetchInvitations(state?: string): Promise<InvitationList> {
  const { data } = await apiClient.get<InvitationList>('/team/invitations', {
    params: state ? { state } : undefined,
  });
  return data;
}

export async function sendInvitation(
  body: InvitationRequest,
): Promise<Invitation> {
  const { data } = await apiClient.post<Invitation>('/team/invitations', body);
  return data;
}

export async function revokeInvitation(invitationId: string): Promise<Invitation> {
  const { data } = await apiClient.delete<Invitation>(
    `/team/invitations/${invitationId}`,
  );
  return data;
}

export async function resendInvitation(invitationId: string): Promise<Invitation> {
  const { data } = await apiClient.post<Invitation>(
    `/team/invitations/${invitationId}/resend`,
  );
  return data;
}

// ---------- Strategy collaborators ----------

export async function fetchCollaborators(
  strategyId: string,
): Promise<CollaboratorList> {
  const { data } = await apiClient.get<CollaboratorList>(
    `/strategies/${strategyId}/collaborators`,
  );
  return data;
}

export async function addCollaborator(
  strategyId: string,
  body: CollaboratorAddRequest,
): Promise<Collaborator> {
  const { data } = await apiClient.post<Collaborator>(
    `/strategies/${strategyId}/collaborators`,
    body,
  );
  return data;
}

export async function updateCollaborator(
  strategyId: string,
  userId: string,
  body: CollaboratorUpdateRequest,
): Promise<Collaborator> {
  const { data } = await apiClient.put<Collaborator>(
    `/strategies/${strategyId}/collaborators/${userId}`,
    body,
  );
  return data;
}

export async function removeCollaborator(
  strategyId: string,
  userId: string,
): Promise<void> {
  await apiClient.delete(`/strategies/${strategyId}/collaborators/${userId}`);
}

// ---------- Join requests ----------
//
// Replaces silent domain auto-join: matching a workspace's verified email
// domain now only decides what you are OFFERED, and an admin decides what is
// granted. Targets come from the session's own address — there is deliberately
// no way to ask about somebody else's.

export async function fetchJoinTargets(): Promise<JoinTargets> {
  const { data } = await apiClient.get<JoinTargets>(
    '/team/join-requests/targets',
  );
  return data;
}

export async function createJoinRequest(
  body: JoinRequestCreate,
): Promise<JoinRequest> {
  const { data } = await apiClient.post<JoinRequest>('/team/join-requests', body);
  return data;
}

export async function fetchJoinRequests(
  state?: string,
): Promise<{ requests: Array<JoinRequest> }> {
  const { data } = await apiClient.get<{ requests: Array<JoinRequest> }>('/team/join-requests', {
    params: state ? { state } : undefined,
  });
  return data;
}

export async function decideJoinRequest(
  id: string,
  body: JoinRequestDecision,
): Promise<JoinRequest> {
  const { data } = await apiClient.post<JoinRequest>(
    `/team/join-requests/${id}/decision`,
    body,
  );
  return data;
}

export async function cancelJoinRequest(id: string): Promise<JoinRequest> {
  const { data } = await apiClient.delete<JoinRequest>(
    `/team/join-requests/${id}`,
  );
  return data;
}

// ---------- Review access (temporary, read-only outside reviewers) ----------

export async function fetchReviewGrants(): Promise<ReviewGrantList> {
  const { data } = await apiClient.get<ReviewGrantList>('/team/review-access');
  return data;
}

export async function grantReviewAccess(
  body: ReviewGrantRequest,
): Promise<ReviewGrant> {
  const { data } = await apiClient.post<ReviewGrant>(
    '/team/review-access',
    body,
  );
  return data;
}

export async function revokeReviewAccess(grantId: string): Promise<ReviewGrant> {
  const { data } = await apiClient.delete<ReviewGrant>(
    `/team/review-access/${grantId}`,
  );
  return data;
}
